import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaTrash } from "react-icons/fa";
import {
  DangerButton,
  Modal,
  PrimaryButton,
  SecondaryButton,
  formatSquadInfoShort,
} from "../UI/DesignStandards";
import InlineAlert from "../UI/DesignStandards/Notifications/InlineAlert";
import { createLeagueSheetSignedUrl } from "../../utility/averageCatalogs";
import { laneAssignmentLabel } from "../../utility/laneAssignment";
import {
  AVERAGES_BLOCK_VERIFY_REASON,
  clearTeamRecapSheet,
  scoreReviewBlockedReason,
  showScoresWrittenWaitingCue,
  verifyBlockedOnlyOnAverages,
} from "../../utility/recapSheets";
import { upsertTeamScoresVerification } from "../../utility/teamScoreVerification";
import { sanitizeErrorMessage } from "../../utility/errorSanitization";
import { SheetRowPreview } from "../organizations/AverageCatalogs/SheetRowPreview";
import ScoreReviewGames from "./ScoreReviewGames";
import ScoresWrittenWaitingNote from "./Views/TeamScoreCard/ScoresWrittenWaitingNote";

const formatCount = (value) => Number(value || 0).toLocaleString("en-US");

const reviewBtnClass =
  "!h-10 !min-w-[12.5rem] !px-3 !py-2 text-sm font-display font-bold tracking-wide !rounded-lg justify-center whitespace-nowrap";
const reviewConfirmBtnClass =
  "!h-10 !min-w-[5.5rem] !px-3 !py-2 text-sm font-display font-bold tracking-wide !rounded-lg justify-center whitespace-nowrap";

const ScoreReview = ({
  isOpen,
  onClose,
  teamIds = [],
  startIndex = 0,
  teamsById = {},
  tournamentId,
  canEnterScores = false,
  canVerifyTeamForEdit,
  verificationStatus,
  onScoreUpdate,
  onVerificationChange,
  calculatingTotals,
  getCalculationResult,
  isScoreSaving,
  canManage = false,
  onRecapDelete,
  importedTeamIds = [],
}) => {
  const [index, setIndex] = useState(0);
  const [url, setUrl] = useState("");
  const [urlError, setUrlError] = useState("");
  const [paneWidth, setPaneWidth] = useState(560);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [showRecapDeleteConfirmation, setShowRecapDeleteConfirmation] =
    useState(false);
  const [deletingRecap, setDeletingRecap] = useState(false);
  const previewRef = useRef(null);
  const urlByPath = useRef(new Map());
  const openedRef = useRef(false);
  const indexRef = useRef(0);
  const lastRowRef = useRef(false);
  const savingRef = useRef(false);
  const goToRef = useRef(() => {});

  const teamId = teamIds[index];
  const team = teamsById[teamId] || null;
  const lastRow = index >= teamIds.length - 1;
  const payoutsLocked = Boolean(verificationStatus?.payouts?.isVerified);
  const isTeamVerified = Boolean(team?.isVerified);
  const blockedReason = scoreReviewBlockedReason({
    team,
    payoutsLocked,
    canVerifyTeamForEdit,
  });
  const waitingOnAverages = verifyBlockedOnlyOnAverages({
    team,
    payoutsLocked,
  });
  const scoresJustWritten = showScoresWrittenWaitingCue({
    team,
    importedTeamIds,
    payoutsLocked,
  });
  const laneLabel = team ? laneAssignmentLabel(team) : "";
  const squadLabel = team?.squad?.squadDate
    ? formatSquadInfoShort(team.squad.squadDate, team.squad.squadTime)
    : "";
  const teamScratchTotal =
    getCalculationResult?.(team?.id, "team", "scratch")?.teamScratchTotal || 0;
  const teamHandicapTotal =
    getCalculationResult?.(team?.id, "team", "handicap")?.teamHandicapTotal ||
    0;
  const isCalculatingTotals =
    calculatingTotals && typeof calculatingTotals.has === "function"
      ? calculatingTotals.has(team?.id)
      : false;

  useEffect(() => {
    if (!isOpen) {
      openedRef.current = false;
      return;
    }
    if (openedRef.current) return;
    openedRef.current = true;
    const nextIndex = Math.min(
      Math.max(startIndex, 0),
      Math.max(teamIds.length - 1, 0)
    );
    setIndex(nextIndex);
    setSaveError("");
    setShowRecapDeleteConfirmation(false);
  }, [isOpen, startIndex, teamIds]);

  useEffect(() => {
    if (!isOpen) return;
    if (!teamIds.length) return;
    if (index > teamIds.length - 1) {
      setIndex(teamIds.length - 1);
    }
  }, [isOpen, teamIds.length, index]);

  useEffect(() => {
    setShowRecapDeleteConfirmation(false);
  }, [teamId]);

  useEffect(() => {
    let cancelled = false;
    const storagePath = team?.recapStoragePath;
    if (!isOpen || !storagePath) {
      setUrl("");
      setUrlError(team ? "This recap file is not stored." : "");
      return undefined;
    }
    const cached = urlByPath.current.get(storagePath);
    if (cached) {
      setUrl(cached);
      setUrlError("");
      return undefined;
    }
    setUrl("");
    setUrlError("");
    createLeagueSheetSignedUrl(storagePath, 3600)
      .then((signedUrl) => {
        urlByPath.current.set(storagePath, signedUrl);
        if (!cancelled) setUrl(signedUrl);
      })
      .catch((err) => {
        console.error(err);
        if (!cancelled) {
          setUrlError(err.message || "Could not open this recap.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, team?.recapStoragePath]);

  useEffect(() => {
    if (!previewRef.current) return undefined;
    const pane = previewRef.current;
    const updateWidth = () => setPaneWidth(pane.clientWidth || 560);
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(pane);
    return () => observer.disconnect();
  }, [url, isOpen, index]);

  const goTo = (nextIndex) => {
    if (nextIndex < 0 || nextIndex >= teamIds.length) return;
    setSaveError("");
    setIndex(nextIndex);
  };

  const handleSkip = () => {
    if (lastRow) {
      onClose();
      return;
    }
    goTo(index + 1);
  };

  const handleDeleteRecap = async () => {
    if (!team || payoutsLocked || isTeamVerified || !canManage) return;
    setDeletingRecap(true);
    setSaveError("");
    try {
      await clearTeamRecapSheet(team.id);
      onRecapDelete?.(team.id);
      setShowRecapDeleteConfirmation(false);
    } catch (err) {
      console.error(err);
      setSaveError(sanitizeErrorMessage(err, "delete recap"));
    } finally {
      setDeletingRecap(false);
    }
  };

  const handleVerify = async () => {
    if (!team) return;
    if (isTeamVerified) {
      handleSkip();
      return;
    }
    if (blockedReason) {
      setSaveError(blockedReason);
      return;
    }
    setSaving(true);
    setSaveError("");
    try {
      onVerificationChange?.(team.id, true);
      await upsertTeamScoresVerification({
        tournamentId,
        teamId: team.id,
        isVerified: true,
      });
      if (lastRow) {
        onClose();
        return;
      }
      goTo(index + 1);
    } catch (err) {
      console.error(err);
      setSaveError(sanitizeErrorMessage(err, "verify team"));
    } finally {
      setSaving(false);
    }
  };

  const busy = saving || deletingRecap;
  goToRef.current = goTo;
  indexRef.current = index;
  lastRowRef.current = lastRow;
  savingRef.current = busy;

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (event) => {
      if (savingRef.current) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const tag = event.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (event.key === "ArrowRight" && !lastRowRef.current) {
        event.preventDefault();
        goToRef.current(indexRef.current + 1);
      }
      if (event.key === "ArrowLeft" && indexRef.current > 0) {
        event.preventDefault();
        goToRef.current(indexRef.current - 1);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen]);

  const primaryLabel = isTeamVerified
    ? lastRow
      ? "Done"
      : "Next"
    : lastRow
    ? "Verify and finish"
    : "Verify and next";

  const subtitle = teamIds.length
    ? [squadLabel, `${formatCount(index + 1)} of ${formatCount(teamIds.length)}`]
        .filter(Boolean)
        .join(" · ")
    : "No scores left to review.";

  const teamTitle = useMemo(() => {
    if (!team) return "Score review";
    const name = team.teamName || "Team";
    return laneLabel ? `${name} · ${laneLabel}` : name;
  }, [team, laneLabel]);

  const highlightNames = useMemo(
    () => (team?.bowlers || []).map((bowler) => bowler.name).filter(Boolean),
    [team]
  );
  const firstBowler = team?.bowlers?.[0];
  const firstImportedGame =
    team?.transformedScores?.[firstBowler?.id]?.[0]?.score ?? null;

  const progressPct = teamIds.length
    ? ((index + 1) / teamIds.length) * 100
    : 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={teamTitle}
      subtitle={subtitle}
      maxWidth="max-w-7xl"
      className="h-[95vh] !max-h-[95vh] sm:h-[95vh] sm:!max-h-[95vh]"
      bodyClassName="!overflow-hidden flex flex-col !pt-0"
      footer={
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-3">
            <SecondaryButton
              type="button"
              onClick={onClose}
              disabled={busy}
              className={reviewBtnClass}
            >
              Cancel
            </SecondaryButton>
            <SecondaryButton
              type="button"
              onClick={() => goTo(index - 1)}
              disabled={!team || index === 0 || busy}
              className={reviewBtnClass}
            >
              Previous
            </SecondaryButton>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {canManage && team && !isTeamVerified ? (
              showRecapDeleteConfirmation ? (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-red-600 font-medium whitespace-nowrap">
                    Delete recap?
                  </span>
                  <DangerButton
                    type="button"
                    onClick={handleDeleteRecap}
                    disabled={busy || payoutsLocked}
                    className={reviewConfirmBtnClass}
                    aria-label="Confirm delete recap"
                  >
                    Yes
                  </DangerButton>
                  <SecondaryButton
                    type="button"
                    onClick={() => setShowRecapDeleteConfirmation(false)}
                    disabled={busy}
                    className={reviewConfirmBtnClass}
                    aria-label="Cancel delete recap"
                  >
                    No
                  </SecondaryButton>
                </div>
              ) : (
                <DangerButton
                  type="button"
                  onClick={() => setShowRecapDeleteConfirmation(true)}
                  disabled={busy || payoutsLocked}
                  icon={FaTrash}
                  className={reviewBtnClass}
                  aria-label="Delete recap"
                  title={
                    payoutsLocked
                      ? "Payouts have been locked and cannot be changed"
                      : "Delete recap"
                  }
                >
                  Delete recap
                </DangerButton>
              )
            ) : null}
            {!isTeamVerified ? (
              <SecondaryButton
                type="button"
                onClick={handleSkip}
                disabled={!team || busy}
                className={reviewBtnClass}
              >
                Skip
              </SecondaryButton>
            ) : null}
            <PrimaryButton
              type="button"
              onClick={handleVerify}
              disabled={
                !team || busy || (!isTeamVerified && Boolean(blockedReason))
              }
              loading={saving}
              className={reviewBtnClass}
              title={
                !isTeamVerified && blockedReason ? blockedReason : undefined
              }
            >
              {primaryLabel}
            </PrimaryButton>
          </div>
        </div>
      }
    >
      {team ? (
        <div className="flex-1 min-h-0 flex flex-col gap-4">
          {scoresJustWritten ? (
            <div className="shrink-0">
              <ScoresWrittenWaitingNote />
            </div>
          ) : waitingOnAverages ? (
            <p className="text-sm text-brand-charcoal shrink-0">
              {AVERAGES_BLOCK_VERIFY_REASON}
            </p>
          ) : null}
          <div
            className="h-1 bg-brand-cream shrink-0 -mx-4 sm:-mx-6"
            role="progressbar"
            aria-valuemin={1}
            aria-valuemax={teamIds.length}
            aria-valuenow={index + 1}
            aria-label="Review progress"
          >
            <div
              className="h-full bg-brand-gold transition-[width] duration-200"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          {saveError ? (
            <InlineAlert
              type="error"
              title="Could not verify"
              message={saveError}
              className="shrink-0"
            />
          ) : null}
          <div className="flex-1 min-h-0 flex flex-col gap-4 lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(22rem,28rem)] lg:grid-rows-1">
            <div className="min-h-[12rem] flex-1 lg:min-h-0 flex flex-col h-full">
              {!team.recapStoragePath ? (
                <div className="flex-1 min-h-0 flex items-center justify-center border border-brand-charcoal/10 rounded-lg bg-brand-cream p-6">
                  <p className="text-sm text-brand-muted text-center">
                    No recap for this team.
                  </p>
                </div>
              ) : urlError ? (
                <p className="text-sm text-brand-charcoal">{urlError}</p>
              ) : !url ? (
                <p className="text-sm text-brand-muted">Opening recap.</p>
              ) : (
                <div
                  ref={previewRef}
                  className="flex-1 min-h-0 h-0 overflow-hidden border border-brand-charcoal/10 rounded-lg bg-brand-cream p-3"
                >
                  <SheetRowPreview
                    url={url}
                    storagePath={team.recapStoragePath}
                    mimeType={team.recapMimeType}
                    fileName={team.recapFileName}
                    boundingBox={team.recapBoundingBox}
                    preferBoundingBox={Boolean(team.recapBoundingBox)}
                    fitHighlight
                    paintHighlight={false}
                    highlightName={highlightNames[0] || ""}
                    highlightNames={highlightNames}
                    highlightAverage={firstBowler?.average}
                    highlightGames={firstImportedGame}
                    containerWidth={paneWidth}
                    toolbarLeft={
                      <div className="min-w-0">
                        <p
                          className="text-sm font-medium text-brand-charcoal truncate"
                          title={team.recapFileName || "Recap"}
                        >
                          {team.recapFileName || "Recap"}
                        </p>
                        {team.recapBoundingBox ? null : (
                          <p className="text-xs text-brand-muted">
                            Compare by names and lanes.
                          </p>
                        )}
                      </div>
                    }
                  />
                </div>
              )}
            </div>
            <aside className="min-h-0 overflow-y-auto border border-brand-charcoal/10 rounded-lg bg-white">
              <ScoreReviewGames
                team={team}
                bowlers={team.bowlers || []}
                gamesPerEvent={team.gamesPerEvent || 3}
                canEnterScores={canEnterScores}
                isTeamVerified={isTeamVerified}
                payoutsLocked={payoutsLocked}
                onScoreUpdate={onScoreUpdate}
                getCalculationResult={getCalculationResult}
                isCalculatingTotals={isCalculatingTotals}
                teamScratchTotal={teamScratchTotal}
                teamHandicapTotal={teamHandicapTotal}
                squadLabel={squadLabel}
              />
            </aside>
          </div>
        </div>
      ) : (
        <p className="text-sm text-brand-muted">No scores left to review.</p>
      )}
    </Modal>
  );
};

export default ScoreReview;
